import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { TripListDisplay } from "../../../components/TripListDisplay";
import { VipTagSelect } from "../../../components/VipTagSelect";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { useCreateTrip } from "../../../hooks/useCreateTrip";
import { useVipTags } from "../../../hooks/useVipTags";
import { TripService } from "../../../services/TripService";
import {
  Aircraft,
  Hangar,
  Location,
  Ramp,
  Tenant,
  TenantType,
  Trip,
  TripWithTenant,
} from "../../../types";
import { SCHEDULE_MIN_DATE } from "../../../utils/dateConstants";
import {
  INFINITY_DATE_STRING,
  isInfinityDate,
} from "../../../utils/dateHelpers";
import { SelectTailNumber } from "../../../widgets/SelectTailNumber";
import { tenantTypeDropdownColors } from "../../../utils/tripTypeHelpers";
import { AutocompleteAircraft } from "./AutocompleteAircraft";

export interface AddTripTabRef {
  isFormValid: boolean;
  loading: boolean;
  createTrip: () => Promise<void>;
}

interface AddTripTabProps {
  location: Location<Hangar | Ramp>;
  currentTime?: Date;
  tenants: Tenant[];
  onAddTrip?: (trip: Trip) => void;
  onClose: () => void;
  onStateChange?: (isValid: boolean, loading: boolean) => void;
}

export const AddTripTab = React.forwardRef<AddTripTabRef, AddTripTabProps>(
  (
    { location, currentTime, tenants, onAddTrip, onClose, onStateChange },
    ref
  ) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
    const { activeFBO } = useActiveFBOs();
    const { postgrest } = useApi();
    const snackbar = useSnackbar();
    const { vipTags } = useVipTags(activeFBO?.id);

    // Initialize shared hook with initial form data
    const initialFormData = React.useMemo(
      () => ({
        arrivalDate: currentTime || new Date(),
        departureDate: currentTime
          ? addDays(currentTime, 2)
          : addDays(new Date(), 2),
      }),
      [currentTime]
    );

    const {
      formData,
      setFormData,
      loading,
      error,
      validationErrors,
      createTrip,
      validateForm,
      setError,
      checkForOverlaps,
      isFormValid,
    } = useCreateTrip(initialFormData);

    // Services for trip retrieval
    const tripService = React.useMemo(
      () => new TripService(postgrest),
      [postgrest]
    );

    // Existing trips state
    const [existingTrips, setExistingTrips] = React.useState<TripWithTenant[]>(
      []
    );

    // Trip creation handlers
    const handleTripTailNumberChange = (value: string) => {
      setFormData({ tailNumber: value });
    };

    const handleTripTenantChange = (tenant: Tenant | null) => {
      if (tenant) {
        setFormData({
          selectedTenant: tenant,
          tailNumber: tenant.tail_number || "",
          ownerManager: tenant.owner_manager || "",
          tenantType: tenant.type || TenantType.TRANSIENT,
          selectedAircraft: tenant.aircraft || null,
          vipTagIds: tenant.vip_tag_ids || [],
        });

        // Fetch existing trips for this tenant
        fetchExistingTrips(tenant.tail_number);
      } else {
        setFormData({ selectedTenant: null, vipTagIds: [] });
        setExistingTrips([]);
      }
    };

    // Fetch existing trips for tail number
    const fetchExistingTrips = async (tailNumber: string) => {
      if (!activeFBO?.id || !tailNumber.trim()) {
        setExistingTrips([]);
        return;
      }

      try {
        const trips = await tripService.getTripsByTailNumber(
          activeFBO.id,
          tailNumber
        );
        setExistingTrips(trips);
      } catch (error) {
        console.error("Failed to fetch existing trips:", error);
        setExistingTrips([]);
      }
    };

    // Handle trip creation
    const handleCreateTrip = React.useCallback(async () => {
      try {
        const trip = await createTrip({
          locationId: location?.id,
          locationType: location?.type as "hangar" | "ramp" | undefined,
          useGapFilling: true, // Use gap filling for AddTripTab
        });

        // Call success callback
        if (onAddTrip) {
          onAddTrip(trip);
        }

        // Reset form and close dialog
        onClose();

        snackbar.notify({
          text: `Trip created for ${formData.tailNumber}`,
          severity: "success",
        });
      } catch (err: any) {
        console.error("Failed to create trip:", err);

        // Check if error is due to overlapping trips (database constraint)
        if (err.message && err.message.toLowerCase().includes("overlap")) {
          setError(
            t("settings.hangarSettings.addTripTab.messages.overlapError")
          );
          // Refresh existing trips to show the latest data
          if (formData.selectedTenant) {
            fetchExistingTrips(formData.selectedTenant.tail_number);
          }
        }
        // Error is already set by the shared hook for other cases
      }
    }, [
      createTrip,
      location?.id,
      location?.type,
      onAddTrip,
      onClose,
      snackbar,
      formData.tailNumber,
      formData.selectedTenant,
      setError,
      fetchExistingTrips,
    ]);

    // Check for overlaps when dates change
    const hasOverlap = React.useMemo(() => {
      if (
        !formData.arrivalDate ||
        !formData.departureDate ||
        !existingTrips.length
      ) {
        return false;
      }
      return (
        checkForOverlaps(
          formData.arrivalDate,
          formData.departureDate,
          existingTrips
        ) !== null
      );
    }, [
      formData.arrivalDate,
      formData.departureDate,
      existingTrips,
      checkForOverlaps,
    ]);

    // Update validation with existing trips
    React.useEffect(() => {
      if (formData.arrivalDate && formData.departureDate) {
        validateForm(existingTrips);
      }
    }, [
      formData.arrivalDate,
      formData.departureDate,
      existingTrips,
      validateForm,
    ]);

    // Calculate if form is valid including overlap check
    const isFormValidWithOverlapCheck = React.useMemo(() => {
      return isFormValid && !hasOverlap;
    }, [isFormValid, hasOverlap]);

    // Expose form state via ref
    React.useImperativeHandle(ref, () => ({
      isFormValid: isFormValidWithOverlapCheck,
      loading,
      createTrip: handleCreateTrip,
    }));

    // Track "No departure" checkbox state
    const [noScheduledDeparture, setNoScheduledDeparture] =
      React.useState(false);

    // Sync checkbox state with formData.departureDate
    React.useEffect(() => {
      setNoScheduledDeparture(isInfinityDate(formData.departureDate));
    }, [formData.departureDate]);

    // Notify parent of state changes for DialogActions
    const onStateChangeRef = React.useRef(onStateChange);
    React.useEffect(() => {
      onStateChangeRef.current = onStateChange;
    }, [onStateChange]);

    React.useEffect(() => {
      onStateChangeRef.current?.(isFormValidWithOverlapCheck, loading);
    }, [isFormValidWithOverlapCheck, loading]);

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack
          spacing={isTablet ? 1.25 : 2}
          sx={{
            height: "100%",
            overflowY: "visible",
            pt: isTablet ? 0.5 : 1,
            minHeight: 0,
          }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <SelectTailNumber
            disableAutocomplete={false}
            defaultValue=""
            tenants={tenants}
            onInputChange={handleTripTailNumberChange}
            onChange={handleTripTenantChange}
            warning={validationErrors.tailNumber}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={t('settings.hangarSettings.addTripTab.labels.ownerManager')}
              value={formData.ownerManager}
              onChange={(e) => setFormData({ ownerManager: e.target.value })}
              fullWidth
              size="small"
              placeholder={t('settings.hangarSettings.addTripTab.placeholders.ownerManager')}
            />

            <TextField
              select
              label={t('settings.hangarSettings.addTripTab.labels.type')}
              value={formData.tenantType}
              onChange={(e) => setFormData({ tenantType: e.target.value })}
              size="small"
              sx={{ minWidth: 160 }}
              SelectProps={{
                renderValue: (value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: tenantTypeDropdownColors[value as string] || '#999',
                      flexShrink: 0,
                    }} />
                    {t(`settings.hangarSettings.addTripTab.tenantTypes.${value}`)}
                  </Box>
                ),
              }}
            >
              <MenuItem value={TenantType.TRANSIENT}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tenantTypeDropdownColors.transient }} />
                  {t('settings.hangarSettings.addTripTab.tenantTypes.transient')}
                </Box>
              </MenuItem>
              <MenuItem value={TenantType.BASE}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tenantTypeDropdownColors.base }} />
                  {t('settings.hangarSettings.addTripTab.tenantTypes.base')}
                </Box>
              </MenuItem>
            </TextField>
            <VipTagSelect
              value={formData.vipTagIds || []}
              availableTags={vipTags}
              onChange={(tagIds) => setFormData({ vipTagIds: tagIds })}
            />
          </Stack>

          <Stack spacing={1}>
            <AutocompleteAircraft
              label={t('settings.hangarSettings.bulkAdd.labels.searchByMakeModel')}
              onSelect={(aircraft) =>
                setFormData({ selectedAircraft: aircraft })
              }
              defaultSelected={(formData.selectedAircraft as Aircraft) || undefined}
            />
            {validationErrors.aircraft && (
              <Typography variant="caption" color="error">
                {validationErrors.aircraft}
              </Typography>
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              <FlightLandIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 0.5 }}
                color="action"
              />
              {t('settings.hangarSettings.addTripTab.labels.arrivalDateTime')} <span style={{ color: "red" }}>*</span>
            </Typography>
            <DateTimePicker
              label=""
              value={formData.arrivalDate}
              onChange={(newValue) => setFormData({ arrivalDate: newValue })}
              minDate={SCHEDULE_MIN_DATE}
              ampm={false}
              minutesStep={15}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: !!validationErrors.arrivalDate,
                  helperText: validationErrors.arrivalDate,
                },
              }}
            />
          </Stack>

          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                <FlightTakeoffIcon
                  fontSize="small"
                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                  color="action"
                />
                {t('settings.hangarSettings.addTripTab.labels.departureDateTime')}{" "}
                {!noScheduledDeparture && (
                  <span style={{ color: "red" }}>*</span>
                )}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noScheduledDeparture}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setNoScheduledDeparture(checked);
                      if (checked) {
                        // Set departure to infinity
                        setFormData({ departureDate: INFINITY_DATE_STRING });
                      } else {
                        // Reset to default (2 days from arrival)
                        const defaultDeparture = formData.arrivalDate
                          ? addDays(formData.arrivalDate, 2)
                          : addDays(new Date(), 2);
                        setFormData({ departureDate: defaultDeparture });
                      }
                    }}
                    disabled={loading}
                  />
                }
                label={t('settings.hangarSettings.addTripTab.labels.noDeparture')}
              />
            </Stack>

            {!noScheduledDeparture ? (
              <DateTimePicker
                label=""
                value={
                  typeof formData.departureDate === "string"
                    ? null
                    : formData.departureDate
                }
                onChange={(newValue) =>
                  setFormData({ departureDate: newValue })
                }
                minDate={
                  formData.arrivalDate &&
                  formData.arrivalDate > SCHEDULE_MIN_DATE
                    ? formData.arrivalDate
                    : SCHEDULE_MIN_DATE
                }
                ampm={false}
                minutesStep={15}
                minDateTime={formData.arrivalDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!validationErrors.departureDate,
                    helperText: validationErrors.departureDate,
                  },
                }}
              />
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('settings.hangarSettings.addTripTab.messages.noDepartureInfo')}
              </Alert>
            )}
          </Stack>

          <TextField
            label={t('settings.hangarSettings.addTripTab.labels.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ notes: e.target.value })}
            multiline
            rows={isTablet ? 2 : 3}
            size="small"
            placeholder={t('settings.hangarSettings.addTripTab.placeholders.notes')}
            fullWidth
          />

          {/* Show existing trips if tenant is selected and has trips */}
          {formData.selectedTenant && existingTrips.length > 0 && (
            <Alert severity={hasOverlap ? "error" : "warning"}>
              <Typography variant="body2" gutterBottom>
                <strong>
                  {t('settings.hangarSettings.addTripTab.messages.existingTrips', { tailNumber: formData.selectedTenant.tail_number })}
                </strong>
              </Typography>
              <TripListDisplay trips={existingTrips} compact={true} />
              {hasOverlap && validationErrors.overlap && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1, fontWeight: 500 }}
                >
                  ⚠️ {validationErrors.overlap}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", fontStyle: "italic" }}
              >
                {hasOverlap
                  ? t('settings.hangarSettings.addTripTab.messages.avoidOverlap')
                  : t('settings.hangarSettings.addTripTab.messages.checkDatesWarning')}
              </Typography>
            </Alert>
          )}

          {location && (
            <Alert severity="info">
              {t('settings.hangarSettings.addTripTab.messages.autoAssignedInfo', { locationType: location.type })}
            </Alert>
          )}
        </Stack>
      </LocalizationProvider>
    );
  }
);

AddTripTab.displayName = "AddTripTab";
